<template>
    <div class="main">
        <c-panel :title="modoEdicao ? 'EDITAR INDICADOR PREDITIVO' : 'NOVO INDICADOR PREDITIVO'" titleIcon="fas fa-brain">
            <el-tabs v-model="aba">

                <!-- ==================== CONFIGURAÇÃO (item 138) ==================== -->
                <el-tab-pane label="CONFIGURAÇÃO" name="config">
                    <el-form ref="dados" :model="dados" label-position="top" @submit.native.prevent="salvar">

                        <el-row :gutter="10">
                            <el-col :xs="24" :sm="10">
                                <el-form-item label="Nome" prop="nome" :rules="$requiredBlur">
                                    <el-input v-model="dados.nome" placeholder="Ex.: Risco de evasão — Fundamental II 2026"></el-input>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="7">
                                <el-form-item label="Objetivo" prop="objetivo" :rules="$requiredChange">
                                    <el-select v-model="dados.objetivo" style="width: 100%;" :disabled="temModelo">
                                        <el-option v-for="o in objetivos" :key="o.valor" :value="o.valor" :label="o.rotulo"></el-option>
                                    </el-select>
                                    <small v-if="temModelo">O objetivo não muda depois do treino — o modelo aprendeu a prever outra coisa.</small>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="7">
                                <el-form-item label="Período letivo" prop="periodo_letivo_id" :rules="$requiredChange">
                                    <el-select v-model="dados.periodo_letivo_id" style="width: 100%;">
                                        <el-option v-for="p in periodos" :key="p.id" :value="p.id" :label="p.nome"></el-option>
                                    </el-select>
                                </el-form-item>
                            </el-col>
                        </el-row>

                        <h4 class="u-mt-10 u-mb-10">Recorte (item 138)</h4>
                        <el-alert type="info" :closable="false" class="u-mb-15"
                            title="Restringe quais turmas entram no cálculo e no treino. Em branco = não restringe."></el-alert>

                        <el-row :gutter="10">
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Escolas">
                                    <el-select v-model="dados.instituicoes" multiple filterable collapse-tags style="width: 100%;">
                                        <el-option v-for="i in instituicoes" :key="i.id" :value="i.id" :label="i.nome"></el-option>
                                    </el-select>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Turnos">
                                    <el-select v-model="dados.turnos" multiple collapse-tags style="width: 100%;">
                                        <el-option v-for="t in turnos" :key="t.valor" :value="t.valor" :label="t.rotulo"></el-option>
                                    </el-select>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Modalidades">
                                    <el-select v-model="dados.modalidades" multiple filterable collapse-tags style="width: 100%;">
                                        <el-option v-for="m in modalidades" :key="m.id" :value="m.id" :label="m.nome"></el-option>
                                    </el-select>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Etapas da matriz">
                                    <el-select v-model="dados.matriz_etapas" multiple filterable collapse-tags style="width: 100%;">
                                        <el-option v-for="e in etapas" :key="e.id" :value="e.id" :label="e.nome"></el-option>
                                    </el-select>
                                </el-form-item>
                            </el-col>
                        </el-row>

                        <h4 class="u-mt-10 u-mb-10">Limiares de risco</h4>

                        <el-row :gutter="10">
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Risco alto a partir de">
                                    <el-input-number v-model="dados.limiar_alto" :min="0.01" :max="0.99" :step="0.05"
                                        :precision="2" controls-position="right" style="width: 100%;"></el-input-number>
                                    <small>{{ pct(dados.limiar_alto) }} de probabilidade</small>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Risco médio a partir de">
                                    <el-input-number v-model="dados.limiar_medio" :min="0.01" :max="0.99" :step="0.05"
                                        :precision="2" controls-position="right" style="width: 100%;"></el-input-number>
                                    <small>{{ pct(dados.limiar_medio) }} de probabilidade</small>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Frequência de execução">
                                    <el-select v-model="dados.frequencia_execucao" style="width: 100%;">
                                        <el-option v-for="f in frequencias" :key="f" :value="f" :label="rotuloFreq(f)"></el-option>
                                    </el-select>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Situação">
                                    <el-switch v-model="dados.situacao" active-text="Ativa" inactive-text="Inativa"></el-switch>
                                </el-form-item>
                            </el-col>
                        </el-row>

                        <el-alert v-if="dados.limiar_medio >= dados.limiar_alto" type="warning" :closable="false" class="u-mb-15"
                            title="O limiar de risco médio precisa ser MENOR que o de risco alto."></el-alert>

                        <div class="u-text-right u-mt-20">
                            <router-link to="/admin/preditivo/configuracao"><el-button>CANCELAR</el-button></router-link>
                            <el-button class="u-ml-10" v-if="podeEscrever" type="primary" native-type="submit">SALVAR</el-button>
                        </div>
                    </el-form>
                </el-tab-pane>

                <!-- ==================== TREINO (item 141) ==================== -->
                <el-tab-pane label="MODELO" name="modelo" :disabled="!modoEdicao">
                    <el-alert v-if="provedor" :type="provedor.servico_python_disponivel ? 'success' : 'info'"
                        :closable="false" class="u-mb-15"
                        :title="'Provedor ativo: ' + provedor.provedor_ativo"
                        :description="provedor.servico_python_disponivel ? 'Serviço de ML externo disponível.' : (provedor.servico_python_motivo + ' ' + provedor.fallback)"></el-alert>

                    <!--
                        Token do serviço de ML. WRITE-ONLY: o backend nunca devolve o valor,
                        só informa se existe. Por isso o campo nasce vazio mesmo com token gravado.
                    -->
                    <div v-if="provedor && podeConfigurarServico" class="token-ml u-mb-15">
                        <span class="rot">Token do serviço de ML
                            <el-tag size="mini" :type="provedor.token_configurado ? 'success' : 'info'">
                                {{ provedor.token_configurado ? 'CONFIGURADO' : 'NÃO CONFIGURADO' }}
                            </el-tag>
                        </span>
                        <el-row :gutter="10">
                            <el-col :xs="24" :sm="16">
                                <el-input v-model="tokenServicoMl" show-password size="small"
                                    placeholder="Cole o token do serviço. Ele é gravado cifrado e não é exibido de novo."></el-input>
                            </el-col>
                            <el-col :xs="24" :sm="8">
                                <el-button size="small" type="primary" icon="el-icon-key" @click="salvarTokenServicoMl">GRAVAR TOKEN</el-button>
                                <el-button class="u-ml-10" v-if="provedor.token_configurado" size="small" @click="removerTokenServicoMl">REMOVER</el-button>
                            </el-col>
                        </el-row>
                        <span class="obs">Só é necessário se o serviço de ML exigir autenticação. Enviado como cabeçalho Authorization.</span>
                    </div>

                    <el-form label-position="top" @submit.native.prevent>
                        <el-row :gutter="10" class="u-mb-15">
                            <el-col :xs="24" :sm="6">
                                <el-form-item label="Anos de histórico" class="u-mb-0">
                                    <el-input-number v-model="anosHistorico" :min="1" :max="10" controls-position="right" style="width: 100%;"></el-input-number>
                                </el-form-item>
                            </el-col>
                            <el-col :xs="24" :sm="18" class="u-text-right u-mt-25">
                                <el-button size="small" icon="el-icon-view" @click="carregarPrevia">VER DATASET</el-button>
                                <el-button class="u-ml-10" v-if="podeEscrever" size="small" type="primary" icon="el-icon-cpu"
                                    :disabled="!previa || !previa.apto_a_treinar" @click="treinar">TREINAR MODELO</el-button>
                                <el-button class="u-ml-10" size="small" icon="el-icon-download" @click="exportar">EXPORTAR CSV</el-button>
                            </el-col>
                        </el-row>
                    </el-form>

                    <div v-if="previa">
                        <el-row :gutter="10" class="u-mb-15">
                            <el-col :xs="12" :sm="6"><div class="kpi"><span class="n">{{ previa.candidatas }}</span><span class="r">Matrículas no histórico</span></div></el-col>
                            <el-col :xs="12" :sm="6"><div class="kpi kpi-ok"><span class="n">{{ previa.com_rotulo }}</span><span class="r">Com desfecho conhecido</span></div></el-col>
                            <el-col :xs="12" :sm="6"><div class="kpi"><span class="n">{{ previa.positivos }}</span><span class="r">Com o desfecho previsto</span></div></el-col>
                            <el-col :xs="12" :sm="6"><div class="kpi"><span class="n">{{ previa.negativos }}</span><span class="r">Sem o desfecho</span></div></el-col>
                        </el-row>

                        <el-alert v-if="!previa.apto_a_treinar" type="warning" :closable="false" class="u-mb-15"
                            title="Ainda não é possível treinar."
                            :description="previa.aviso || 'É preciso um mínimo de matrículas com desfecho conhecido e exemplos dos dois desfechos. Matrícula em curso não é exemplo de nada e fica de fora.'"></el-alert>
                    </div>

                    <h4 class="u-mb-10">Histórico de modelos</h4>
                    <div class="container-table">
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th style="width: 80px;">Versão</th>
                                    <th style="width: 110px;">Treino</th>
                                    <th style="width: 100px;" class="u-text-center">Amostras</th>
                                    <th style="width: 90px;" class="u-text-center">Acurácia</th>
                                    <th style="width: 90px;" class="u-text-center">Precisão</th>
                                    <th style="width: 90px;" class="u-text-center">Recall</th>
                                    <th style="width: 90px;" class="u-text-center">AUC-ROC</th>
                                    <th>Origem</th>
                                    <th style="width: 80px;" class="u-text-center">Ativo</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="m in modelos" :key="m.id">
                                    <td>{{ m.versao }}</td>
                                    <td>{{ dataBr(m.data_treino) }}</td>
                                    <td class="u-text-center">{{ m.qtd_amostras }}</td>
                                    <td class="u-text-center">{{ m.tem_metricas ? pct(m.acuracia) : '—' }}</td>
                                    <td class="u-text-center">{{ m.precisao !== null ? pct(m.precisao) : '—' }}</td>
                                    <td class="u-text-center">{{ m.recall !== null ? pct(m.recall) : '—' }}</td>
                                    <td class="u-text-center">{{ m.auc_roc !== null ? parseFloat(m.auc_roc).toFixed(3) : '—' }}</td>
                                    <td><small>{{ m.rotulo_origem }}</small></td>
                                    <td class="u-text-center">
                                        <el-tag v-if="m.ativo" size="mini" type="success">ativo</el-tag>
                                    </td>
                                </tr>
                                <tr v-if="modelos.length === 0"><td colspan="9" class="u-text-center">Nenhum modelo treinado.</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <small class="u-text-muted">
                        Modelo sem métricas é calibração de contingência — não passou por validação e não
                        deve ser apresentado como treinado.
                    </small>

                    <div v-if="importancia.length" class="u-mt-20">
                        <h4 class="u-mb-10">Peso de cada fator no modelo ativo</h4>
                        <bar-chart :chart-data="dadosImportancia" :height="260"></bar-chart>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </c-panel>
    </div>
</template>
<script>
import BarChart from '@/chart/BarChart.js'

/** BLOCO K — itens 138 e 141. */
import { periodosLetivosMixin } from '@/shared/periodos-letivos'
export default {
    mixins: [periodosLetivosMixin],
    components: { BarChart },
    data: () => ({
        aba: 'config', objetivos: [], modelos: [], frequencias: [], modalidades: [], etapas: [],
        instituicoes: [], turnos: [], provedor: null, catalogoFeatures: [], tokenServicoMl: '',
        anosHistorico: 3, previa: null, temModelo: false, importancia: [],
        dados: { nome: '', objetivo: '', periodo_letivo_id: null, instituicoes: [], turnos: [],
                 modalidades: [], matriz_etapas: [], niveis_escolares: [], modelo: 'logistico',
                 limiar_alto: 0.7, limiar_medio: 0.4, frequencia_execucao: 'semanal', situacao: true }
    }),
    computed: {
        podeEscrever () { return [4, 5, 6, 7].includes(parseInt(this.$store.state.user.tipo)) },
        // O token do serviço de ML é segredo de infraestrutura: o backend só aceita de 4 e 5.
        podeConfigurarServico () { return [4, 5].includes(parseInt(this.$store.state.user.tipo)) },
        modoEdicao () { return !!this.$route.params.id },
        dadosImportancia () {
            return {
                labels: this.importancia.map(i => i.rotulo),
                datasets: [{ label: 'Peso relativo', backgroundColor: '#409EFF',
                             data: this.importancia.map(i => i.peso) }]
            }
        }
    },
    mounted () {
        if (!this.modoEdicao && this.$store.state.config.periodo) this.dados.periodo_letivo_id = this.$store.state.config.periodo.id

        this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/preditivo/configuracao/initForm`,
            this.$store.getters.getHeaderAuth
        ).then(r => {
            const d = r.data || {}
            this.objetivos = d.objetivos || []
            this.frequencias = d.frequencias || []
            this.modalidades = d.modalidades || []
            this.etapas = d.etapas || []
            this.instituicoes = d.instituicoes || []
            this.turnos = d.turnos || []
            this.provedor = d.provedor || null
            this.catalogoFeatures = d.features || []
        }).catch(e => this.$store.commit('throwException', e))

        if (this.modoEdicao) this.carregar()
    },
    methods: {
        carregar () {
            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/preditivo/configuracao/getById/${this.$route.params.id}`,
                this.$store.getters.getHeaderAuth
            ).then(r => {
                const reg = r.data || {}
                if (reg.erro) {
                    window.eventBus.$emit('alert', { msg: this.textoErro(reg.erro), type: 'warning' })
                    this.$router.push('/admin/preditivo/configuracao')
                    return
                }
                Object.keys(this.dados).forEach(k => { if (reg[k] !== undefined && reg[k] !== null) this.dados[k] = reg[k] })
                // Recorte nulo volta como array vazio para o multiselect funcionar.
                ;['instituicoes', 'turnos', 'modalidades', 'matriz_etapas', 'niveis_escolares'].forEach(k => {
                    if (!Array.isArray(this.dados[k])) this.dados[k] = []
                })
                this.dados.limiar_alto = parseFloat(reg.limiar_alto)
                this.dados.limiar_medio = parseFloat(reg.limiar_medio)
                this.temModelo = !!reg.modelo_ativo

                this.montarImportancia(reg.modelo_ativo)
                this.carregarModelos()
            }).catch(e => this.$store.commit('throwException', e))
        },
        montarImportancia (modeloAtivo) {
            this.importancia = []
            if (!modeloAtivo || !modeloAtivo.importancia_features) return

            const rotulos = {}
            this.catalogoFeatures.forEach(f => { rotulos[f.feature] = f.rotulo })

            this.importancia = Object.keys(modeloAtivo.importancia_features)
                .map(k => ({ rotulo: rotulos[k] || k, peso: modeloAtivo.importancia_features[k] }))
                .sort((a, b) => b.peso - a.peso)
                .slice(0, 10)
        },
        carregarModelos () {
            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/preditivo/modelos/${this.$route.params.id}`,
                this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) return
                this.modelos = r.data.itens || []
            }).catch(() => {})
        },
        salvarTokenServicoMl () {
            if (!this.tokenServicoMl) {
                window.eventBus.$emit('alert', { msg: 'Informe o token antes de gravar.', type: 'warning' })
                return
            }
            this.enviarTokenServicoMl(this.tokenServicoMl, 'Token gravado.')
        },
        removerTokenServicoMl () {
            this.$confirm('Remover o token fará o sistema chamar o serviço de ML sem autenticação. Confirma?',
                'Remover token', { confirmButtonText: 'Remover', cancelButtonText: 'Cancelar', type: 'warning' })
                .then(() => this.enviarTokenServicoMl('', 'Token removido.')).catch(() => {})
        },
        enviarTokenServicoMl (token, ok) {
            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/preditivo/definirTokenServicoMl`,
                { token: token }, this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: this.textoErro(r.data.erro), type: 'warning' }); return }
                window.eventBus.$emit('alert', { msg: r.data.mensagem || ok, type: 'success' })
                this.tokenServicoMl = ''
                if (this.provedor) this.provedor.token_configurado = !!r.data.token_configurado
            }).catch(e => this.$store.commit('throwException', e))
        },
        carregarPrevia () {
            this.previa = null
            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/preditivo/previaDataset/${this.$route.params.id}`,
                { anos_historico: this.anosHistorico }, this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: this.textoErro(r.data.erro), type: 'warning' }); return }
                this.previa = r.data
                this.provedor = r.data.provedor || this.provedor
            }).catch(e => this.$store.commit('throwException', e))
        },
        treinar () {
            this.$confirm('Treinar um modelo novo? O modelo ativo atual será substituído — os scores já calculados continuam apontando para a versão que os produziu.',
                'Confirmação', { confirmButtonText: 'Treinar', cancelButtonText: 'Cancelar', type: 'warning' }
            ).then(() => {
                window.eventBus.$emit('loading', true)
                this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/preditivo/treinar/${this.$route.params.id}`,
                    { anos_historico: this.anosHistorico }, this.$store.getters.getHeaderAuth
                ).then(r => {
                    window.eventBus.$emit('loading', false)
                    if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: this.textoErro(r.data.erro), type: 'warning' }); return }

                    const m = r.data.metricas || {}
                    const acc = m.acuracia !== null && m.acuracia !== undefined ? this.pct(m.acuracia) : '—'
                    window.eventBus.$emit('alert', {
                        msg: `${r.data.mensagem} Acurácia ${acc}, AUC ${m.auc_roc !== null && m.auc_roc !== undefined ? parseFloat(m.auc_roc).toFixed(3) : '—'}.`,
                        type: 'success'
                    })
                    this.carregar()
                }).catch(e => { window.eventBus.$emit('loading', false); this.$store.commit('throwException', e) })
            }).catch(() => {})
        },
        exportar () {
            const cfg = JSON.parse(JSON.stringify(this.$store.getters.getHeaderAuth))
            cfg.responseType = 'blob'

            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/preditivo/exportarDataset/${this.$route.params.id}`,
                { anos_historico: this.anosHistorico }, cfg
            ).then(r => {
                const url = window.URL.createObjectURL(new Blob([r.data]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `dataset-preditivo-${this.$route.params.id}.csv`)
                document.body.appendChild(link)
                link.click()
                link.remove()
            }).catch(e => this.$store.commit('throwException', e))
        },
        pct (v) { return v !== null && v !== undefined ? (parseFloat(v) * 100).toFixed(1) + '%' : '—' },
        rotuloFreq (f) {
            return { diaria: 'Diária', semanal: 'Semanal', quinzenal: 'Quinzenal', mensal: 'Mensal' }[f] || f
        },
        dataBr (d) {
            if (!d) return '—'
            const p = d.substring(0, 10).split('-')
            return `${p[2]}/${p[1]}/${p[0]}`
        },
        salvar () {
            this.$refs.dados.validate(valid => {
                if (!valid) { window.eventBus.$emit('alert', { msg: 'Preencha os campos obrigatórios.', type: 'warning' }); return }
                window.eventBus.$emit('loading', true)
                const url = this.modoEdicao
                    ? `${this.$store.state.config.baseUrlApi}/api/preditivo/configuracao/update/${this.$route.params.id}`
                    : `${this.$store.state.config.baseUrlApi}/api/preditivo/configuracao/store`
                this.$axios.post(url, this.dados, this.$store.getters.getHeaderAuth).then(r => {
                    window.eventBus.$emit('loading', false)
                    if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: this.textoErro(r.data.erro), type: 'warning' }); return }
                    window.eventBus.$emit('alert', { msg: 'Configuração salva.', type: 'success' })
                    this.$router.push('/admin/preditivo/configuracao')
                }).catch(e => { window.eventBus.$emit('loading', false); this.$store.commit('throwException', e) })
            })
        },
        textoErro (e) { return typeof e === 'string' ? e : (e.text || 'Não foi possível concluir.') }
    }
}
</script>
<style scoped>
.token-ml { border: 1px dashed #dcdfe6; border-radius: 4px; padding: 10px; }
.token-ml .rot { display: block; font-size: 12px; font-weight: bold; color: #606266; margin-bottom: 6px; }
.token-ml .obs { display: block; font-size: 11px; color: #909399; margin-top: 6px; }
.kpi { border: 1px solid #dcdfe6; border-radius: 4px; padding: 10px; text-align: center; margin-bottom: 8px; }
.kpi .n { display: block; font-size: 20px; font-weight: bold; }
.kpi .r { display: block; font-size: 11px; color: #606266; }
.kpi-ok { border-left: 4px solid #67c23a; }
</style>
